import { makeAutoObservable } from 'mobx';
import type { BibleLocation, Bookmark } from '@skjv/common';
import { isLocationEqual } from '@skjv/common';
import { saveBookmarks, loadBookmarks } from '../persistence';

/**
 * Bookmarks state management.
 */
export class BookmarksState {
  /** User bookmarks */
  bookmarks: Bookmark[] = [];

  constructor() {
    makeAutoObservable(this);

    if (typeof window !== 'undefined') {
      this.bookmarks = loadBookmarks<Bookmark>();
    }
  }

  /** Get bookmarks sorted by favorites first, then alphabetically */
  get sortedBookmarks(): Bookmark[] {
    return [...this.bookmarks].sort((a, b) => {
      if (a.isFavorite && !b.isFavorite) return -1;
      if (!a.isFavorite && b.isFavorite) return 1;
      return a.name.localeCompare(b.name);
    });
  }

  /** Get bookmarks at a specific location */
  getBookmarksAt(location: BibleLocation | null): Bookmark[] {
    if (!location) return [];
    return this.bookmarks.filter((bm) => isLocationEqual(bm, location));
  }

  /** Add a bookmark */
  addBookmark(location: BibleLocation, name: string, isFavorite: boolean = false): void {
    const bookmark: Bookmark = { ...location, name, isFavorite };
    this.bookmarks = [...this.bookmarks, bookmark];
    this.save();
  }

  /** Remove a bookmark by name */
  removeBookmark(name: string): void {
    this.bookmarks = this.bookmarks.filter((bm) => bm.name !== name);
    this.save();
  }

  /** Edit a bookmark */
  editBookmark(oldName: string, newName: string, isFavorite: boolean): void {
    this.bookmarks = this.bookmarks.map((bm) =>
      bm.name === oldName ? { ...bm, name: newName, isFavorite } : bm,
    );
    this.save();
  }

  /** Toggle bookmark favorite status */
  setFavoriteBookmark(name: string, isFavorite: boolean): void {
    this.bookmarks = this.bookmarks.map((bm) => (bm.name === name ? { ...bm, isFavorite } : bm));
    this.save();
  }

  /** Move a bookmark to a new location */
  moveBookmark(name: string, newLocation: BibleLocation): void {
    this.bookmarks = this.bookmarks.map((bm) =>
      bm.name === name ? { ...bm, ...newLocation } : bm,
    );
    this.save();
  }

  /** Import bookmarks (merges by name) */
  importBookmarks(bookmarks: Bookmark[]): number {
    const existingNames = new Set(this.bookmarks.map((b) => b.name));
    let count = 0;
    for (const bm of bookmarks) {
      if (!existingNames.has(bm.name)) {
        this.bookmarks.push(bm);
        count++;
      }
    }
    this.save();
    return count;
  }

  private save(): void {
    saveBookmarks(this.bookmarks);
  }
}
